import { Callout } from '@dev-spendesk/grapes';
import classNames from 'classnames';
import React from 'react';
import { Trans } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { routeFor, routes } from 'src/core/constants/routes';
import { type SpanishDprArchivingStatus } from 'src/core/modules/payable/hooks/api/useSpanishDprArchived';

type Props = {
  isLockedPayable?: boolean;
  wasUnlockablePayable?: boolean;
  spanishDprArchivingStatus?: SpanishDprArchivingStatus;
  className?: string;
};

export const PayablePanelLockFeedback = ({
  isLockedPayable,
  wasUnlockablePayable,
  spanishDprArchivingStatus,
  className = '',
}: Props) => {
  const companyId = useCompanyId();
  const { activeLanguage } = useTranslation('global');

  if (wasUnlockablePayable) {
    return (
      <Callout
        variant="info"
        className={classNames(
          className,
          '[&_a]:text-neutral-darker [&_a]:underline',
        )}
        iconName="lock-close"
        title={
          <Trans
            i18nKey="payables.panel.editionAfterExportDisabled"
            components={{
              a: (
                <Link
                  to={routeFor(
                    routes.COMPANY_ACCOUNTING_BOOKKEEPING_SETUP.path,
                    { company: companyId },
                  )}
                >
                  -
                </Link>
              ),
            }}
          />
        }
      />
    );
  }

  if (spanishDprArchivingStatus?.isArchived) {
    const formatter = new Intl.DateTimeFormat(activeLanguage, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });

    const formattedFromDate = formatter.format(
      new Date(spanishDprArchivingStatus.from),
    );
    const formattedToDate = formatter.format(
      new Date(spanishDprArchivingStatus.to),
    );

    // Show "being archived" for pending/ongoing states
    if (
      spanishDprArchivingStatus.state === 'pending' ||
      spanishDprArchivingStatus.state === 'ongoing'
    ) {
      return (
        <Callout
          variant="info"
          className={classNames(
            className,
            '[&_a]:text-neutral-darker [&_a]:underline',
          )}
          iconName="lock-close"
          title={
            <Trans
              i18nKey="payables.panel.isInSpanishDprArchivingPeriod"
              values={{
                dateFrom: formattedFromDate,
                dateTo: formattedToDate,
              }}
            />
          }
        />
      );
    }

    // Show "have been archived" for completed state
    if (spanishDprArchivingStatus.state === 'completed') {
      return (
        <Callout
          variant="info"
          className={classNames(
            className,
            '[&_a]:text-neutral-darker [&_a]:underline',
          )}
          iconName="lock-close"
          title={
            <Trans
              i18nKey="payables.panel.isInSpanishDprArchivedPeriod"
              values={{
                dateFrom: formattedFromDate,
                dateTo: formattedToDate,
              }}
            />
          }
        />
      );
    }
  }
  if (isLockedPayable) {
    return (
      <Callout
        variant="info"
        className={classNames(
          className,
          '[&_a]:text-neutral-darker [&_a]:underline',
        )}
        iconName="lock-close"
        title={
          <Trans
            i18nKey="payables.panel.isInLockedPeriod"
            components={{
              a: (
                <Link
                  to={routeFor(
                    routes.COMPANY_ACCOUNTING_BOOKKEEPING_SETUP.path,
                    { company: companyId },
                  )}
                >
                  -
                </Link>
              ),
            }}
          />
        }
      />
    );
  }
};
